import React, { useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';

import {
  CreateUpdateEmailAddressDto,
  GetClientDto,
  GetEmailAddressDto,
  ResponseError,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { responseErrors } from '../../../common/pokCore/validation/responseErrors';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { useAuth } from '../../../common/hooks/useAuth';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';
import {
  convert,
  newEmailAddress,
  validate,
} from '../../../common/pokCore/contexts/EmailAddressContext';

import { EmailAddressEditor } from './EmailAddressEditor';
import { EmailAddressSearch } from './EmailAddressSearch';

interface EmailAddressTabProps {
  clientId: string;
  client: GetClientDto;
}

export const EmailAddressTab: React.FC<EmailAddressTabProps> = props => {
  const [show, setShow] = useState<boolean>(false);
  const [emailAddress, setEmailAddress] = useState<GetEmailAddressDto>();
  const [refresh, setRefresh] = useState<boolean>(false);

  const pok = usePokCore();
  const auth = useAuth();
  const { t, tk } = useTranslation('clientLng');

  const hasClients = auth.check(PermissionsEnum.Clients);
  const hasFinance = auth.check(PermissionsEnum.Finance);
  const editable = hasClients || hasFinance;

  const getData = useMemo(
    () =>
      ((pageSize, pageNumber, orderBy, orderDirection, filterText) =>
        pok.emailAddresses.getAllByPortionForClient(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          props.clientId ? props.clientId : undefined,
        )) as GridGetterFunction,
    [pok.emailAddresses, props.clientId],
  );

  const getCount = useMemo(
    () =>
      (filterText =>
        pok.emailAddresses.getAllCountForClient(
          filterText,
          props.clientId ? props.clientId : undefined,
        )) as GridCountFunction,
    [pok.emailAddresses, props.clientId],
  );

  const handleAdd = () => {
    setShow(true);
    setEmailAddress(undefined);
  };

  const handleClose = () => {
    setShow(false);
  };

  const addEditEmailAddress = async (
    emailAddressCU: CreateUpdateEmailAddressDto,
    id?: string,
  ) => {
    const status = await validate(emailAddressCU);
    if (status.valid) {
      try {
        if (id) {
          await pok.emailAddresses.update(id, emailAddressCU);
        } else {
          await pok.emailAddresses.create(emailAddressCU);
        }
        setShow(false);
        setRefresh(!refresh);
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const handleClick = async (id: string) => {
    const getEmailAddress = await pok.emailAddresses.getById(id);
    setEmailAddress(getEmailAddress);
    setShow(true);
  };

  return (
    <>
      {editable && (
        <Button variant="outline-primary" onClick={handleAdd}>
          {t(tk.emailAddress.new)}
        </Button>
      )}
      <EmailAddressSearch
        getData={getData}
        getCount={getCount}
        onRowClick={editable ? handleClick : undefined}
        refresh={refresh}
        withHistory={true}
      />
      <EmailAddressEditor
        id={emailAddress?.id}
        client={props.client}
        emailAddress={
          emailAddress ? convert(emailAddress) : newEmailAddress(props.clientId)
        }
        onCompleted={addEditEmailAddress}
        show={show}
        handleClose={handleClose}
      />
    </>
  );
};
